import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import Header from '../../components/ui/UnifiedHeader';
import SharedFooter from '../../components/SharedFooter';
import { EnhancedBreadcrumb } from '../../components/ui/breadcrumb.jsx';
import { usePageMeta } from '../../components/BreadcrumbProvider.jsx';
import { useAnchorScroll } from '../../hooks/useAnchorScroll';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { useBooking } from '../../contexts/BookingContext';

const ExecutiveHiringLeadershipSearch = () => {
  const { openBooking } = useBooking();
  
  const breadcrumbs = [
    { name: 'Home', href: '/' },
    { name: 'Executive Hiring', href: '/executive-hiring-leadership-search' }
  ];

  // Set page metadata with breadcrumbs
  usePageMeta({
    title: 'Executive Hiring & Leadership Search',
    breadcrumbs: breadcrumbs
  });

  // Handle anchor scrolling
  useAnchorScroll();

  useEffect(() => {
    // Smooth scroll behavior for anchor links
    document.documentElement.style.scrollBehavior = 'smooth';
    
    // Cleanup on unmount
    return () => {
      document.documentElement.style.scrollBehavior = 'auto';
    };
  }, []);

  const leadershipServices = [
    {
      title: 'CXO Search & Selection',
      description: 'Director-to-CXO level executive search with 25-day delivery promise',
      features: [
        'Global talent pool spanning India, Singapore, and Middle East',
        'SkillStack™ assessment methodology',
        'Behavioral and cultural fit evaluation',
        '90-day success integration plan'
      ],
      icon: 'Crown',
      timeline: '25 days',
      guarantee: '90-day replacement guarantee'
    },
    {
      title: 'Leadership Team Building',
      description: 'Complete leadership team assembly for new GCC operations',
      features: [
        'Organizational design and role mapping',
        'Succession planning and bench development',
        'Leadership competency frameworks',
        'Team dynamics and culture alignment'
      ],
      icon: 'Users',
      timeline: '45-60 days',
      guarantee: '6-month performance guarantee'
    },
    {
      title: 'Board & Advisory Services',
      description: 'Independent directors and advisory board member recruitment',
      features: [
        'Industry-specific board composition',
        'Corporate governance expertise',
        'Cross-border experience requirements',
        'Regulatory compliance knowledge'
      ],
      icon: 'Shield',
      timeline: '60-90 days',
      guarantee: '12-month advisory commitment'
    }
  ];

  const hiringProcess = [
    {
      phase: 'Discovery & Strategy',
      duration: '3-5 days',
      activities: [
        'Stakeholder interviews and requirement gathering',
        'Role specification and competency mapping',
        'Market analysis and compensation benchmarking',
        'Search strategy development'
      ]
    },
    {
      phase: 'Talent Mapping & Sourcing',
      duration: '7-10 days',
      activities: [
        'Market mapping and talent identification',
        'Discreet approach and initial screening',
        'SkillStack™ assessment and evaluation',
        'Shortlist preparation and review'
      ]
    },
    {
      phase: 'Evaluation & Selection',
      duration: '5-8 days',
      activities: [
        'In-depth interviews and assessment',
        'Cultural fit and leadership evaluation',
        'Reference and background verification',
        'Final candidate presentation'
      ]
    },
    {
      phase: 'Offer & Onboarding',
      duration: '3-5 days',
      activities: [
        'Offer negotiation and closure',
        'Stakeholder introduction and alignment',
        '90-day onboarding plan execution',
        'Performance integration support'
      ]
    }
  ];

  const expertiseAreas = [
    {
      area: 'Technology Leadership',
      roles: ['CTO', 'CIO', 'VP Engineering', 'Head of Technology'],
      industries: ['SaaS', 'FinTech', 'HealthTech', 'E-commerce']
    },
    {
      area: 'Operations Excellence',
      roles: ['COO', 'VP Operations', 'Head of GCC', 'Operations Director'],
      industries: ['BFSI', 'Healthcare', 'Manufacturing', 'Retail']
    },
    {
      area: 'Financial Leadership',
      roles: ['CFO', 'VP Finance', 'Finance Director', 'Head of Finance'],
      industries: ['Investment Banking', 'Insurance', 'Asset Management', 'Consulting']
    },
    {
      area: 'Human Resources',
      roles: ['CHRO', 'VP HR', 'HR Director', 'Head of TA'],
      industries: ['Technology', 'Professional Services', 'Manufacturing', 'Healthcare']
    }
  ];

  return (
    <>
      <Helmet>
        <title>Executive Hiring & Leadership Search | Bootminds Elite</title>
        <meta 
          name="description" 
          content="25-day executive search promise for GCC leadership. CXO recruitment with SkillStack™ assessment, 90-day integration plans, and guaranteed results across India, Singapore, and Middle East." 
        />
        <meta 
          name="keywords" 
          content="executive hiring, leadership search, CXO recruitment, GCC leadership, executive search India, talent acquisition strategy, leadership assessment" 
        />
        <link rel="canonical" href="/services/executive-hiring" />
      </Helmet>
      
      <div className="min-h-screen bg-background">
        <Header />
        
        {/* Breadcrumb Navigation */}
        <div className="bg-background border-b border-border">
          <div className="container-executive py-4">
            <EnhancedBreadcrumb items={breadcrumbs} />
          </div>
        </div>
        
        <main>
          {/* Hero Section */}
          <section className="py-20 lg:py-24 bg-gradient-to-br from-primary/5 to-accent/5">
            <div className="container-executive">
              <div className="max-w-4xl mx-auto text-center">
                <div className="inline-flex items-center space-x-2 rounded-full bg-primary/10 px-4 py-2 text-sm font-medium text-text-primary mb-6">
                  <Icon name="UserCheck" size={16} />
                  <span>Executive Search Excellence</span>
                </div>
                <h1 className="text-4xl lg:text-6xl font-headline font-bold text-text-primary mb-6">
                  25-Day Executive Search
                  <span className="text-accent"> Promise</span>
                </h1>
                <p className="text-xl text-text-secondary mb-8 leading-relaxed">
                  We meet CXO expectations with SkillStack™ data, behavioral interviews, and curated shortlists. 
                  Every leader comes with a 90-day success plan and governance integration coaching.
                </p>
                <div className="flex justify-center">
                  <Button
                    variant="primary"
                    size="lg"
                    iconName="Calendar"
                    onClick={() => openBooking('executive_hiring_hero')}
                  >
                    Start Executive Search
                  </Button>
                </div>
              </div>
            </div>
          </section>

          {/* Leadership Services */}
          <section className="py-20 bg-background">
            <div className="container-executive">
              <div className="text-center mb-16">
                <h2 className="text-3xl lg:text-4xl font-headline font-bold text-text-primary mb-4">
                  Leadership Search Solutions
                </h2>
                <p className="text-lg text-text-secondary max-w-3xl mx-auto">
                  Comprehensive executive search services tailored for Global Capability Centers and enterprise leadership needs
                </p>
              </div>
              
              <div className="grid gap-8 lg:grid-cols-3">
                {leadershipServices.map((service, index) => (
                  <div key={index} className="group">
                    <div className="h-full bg-card border border-border rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300">
                      <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-primary/20 transition-colors">
                        <Icon name={service.icon} size={32} className="text-primary" />
                      </div>
                      <h3 className="text-xl font-semibold text-text-primary mb-4">{service.title}</h3>
                      <p className="text-text-secondary mb-6">{service.description}</p>
                      <ul className="space-y-3 mb-6">
                        {service.features.map((feature, idx) => (
                          <li key={idx} className="flex items-start space-x-3 text-sm text-text-secondary">
                            <Icon name="Check" size={16} className="text-success mt-0.5 flex-shrink-0" />
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                      <div className="border-t border-border pt-6 space-y-2">
                        <div className="flex justify-between items-center text-sm">
                          <span className="text-text-secondary">Timeline:</span>
                          <span className="font-medium text-text-primary">{service.timeline}</span>
                        </div>
                        <div className="flex justify-between items-center text-sm">
                          <span className="text-text-secondary">Guarantee:</span>
                          <span className="font-medium text-success">{service.guarantee}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Hiring Process */}
          <section id="leadership" className="py-20 bg-background-alt">
            <div className="container-executive">
              <div className="text-center mb-16">
                <h2 className="text-3xl lg:text-4xl font-headline font-bold text-text-primary mb-4">
                  Proven 4-Phase Executive Search Process
                </h2>
                <p className="text-lg text-text-secondary max-w-3xl mx-auto">
                  Systematic approach ensuring quality, speed, and cultural alignment for every executive placement
                </p>
              </div>
              
              <div className="grid gap-8 lg:grid-cols-2">
                {hiringProcess.map((phase, index) => (
                  <div key={index} className="flex gap-6">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 bg-accent rounded-full flex items-center justify-center text-accent-foreground font-bold">
                        {index + 1}
                      </div>
                    </div>
                    <div>
                      <div className="flex items-center gap-3 mb-3">
                        <h3 className="text-xl font-semibold text-text-primary">{phase.phase}</h3>
                        <span className="px-3 py-1 bg-primary/10 text-primary rounded-full text-sm font-medium">
                          {phase.duration}
                        </span>
                      </div>
                      <ul className="space-y-2">
                        {phase.activities.map((activity, idx) => (
                          <li key={idx} className="flex items-start space-x-3 text-text-secondary">
                            <Icon name="ArrowRight" size={16} className="text-primary mt-0.5 flex-shrink-0" />
                            <span className="text-sm">{activity}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Expertise Areas */}
          <section className="py-20 bg-background">
            <div className="container-executive">
              <div className="text-center mb-16">
                <h2 className="text-3xl lg:text-4xl font-headline font-bold text-text-primary mb-4">
                  Industry & Functional Expertise
                </h2>
                <p className="text-lg text-text-secondary max-w-3xl mx-auto">
                  Deep domain knowledge across key leadership functions and industries
                </p>
              </div>
              
              <div className="grid gap-8 lg:grid-cols-2">
                {expertiseAreas.map((expertise, index) => (
                  <div key={index} className="bg-card border border-border rounded-2xl p-8">
                    <h3 className="text-xl font-semibold text-text-primary mb-4">{expertise.area}</h3>
                    <div className="grid gap-6 sm:grid-cols-2">
                      <div>
                        <h4 className="font-medium text-text-primary mb-3 flex items-center gap-2">
                          <Icon name="Briefcase" size={16} className="text-primary" />
                          Key Roles
                        </h4>
                        <ul className="space-y-2">
                          {expertise.roles.map((role, idx) => (
                            <li key={idx} className="text-sm text-text-secondary flex items-center gap-2">
                              <Icon name="ChevronRight" size={12} className="text-primary" />
                              {role}
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <h4 className="font-medium text-text-primary mb-3 flex items-center gap-2">
                          <Icon name="Building2" size={16} className="text-primary" />
                          Industries
                        </h4>
                        <ul className="space-y-2">
                          {expertise.industries.map((industry, idx) => (
                            <li key={idx} className="text-sm text-text-secondary flex items-center gap-2">
                              <Icon name="ChevronRight" size={12} className="text-primary" />
                              {industry}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section className="py-20 bg-gradient-to-br from-primary to-accent text-primary-foreground">
            <div className="container-executive">
              <div className="max-w-4xl mx-auto text-center">
                <h2 className="text-3xl lg:text-4xl font-headline font-bold mb-6">
                  Ready to Build Your Leadership Team?
                </h2>
                <p className="text-xl mb-8 opacity-90">
                  Schedule a confidential consultation with our executive search partners to discuss your leadership requirements.
                </p>
                <div className="flex justify-center">
                  <Button
                    variant="secondary"
                    size="lg"
                    iconName="Calendar"
                    onClick={() => openBooking('executive_hiring_cta')}
                  >
                    Schedule Executive Consultation
                  </Button>
                </div>
              </div>
            </div>
          </section>
        </main>
        <SharedFooter />
      </div>
    </>
  );
};

export default ExecutiveHiringLeadershipSearch;
